import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Map,
    Plus,
    Filter,
    Loader2,
    MapPin,
    Upload,
    RefreshCw,
    List,
    Table2,
    Download,
    Trash2,
    CheckSquare,
    X,
    AlertTriangle,
    ShieldCheck,
    ShieldAlert,
} from 'lucide-react';
import toast from 'react-hot-toast';
import StatusBadge from '../components/polygons/StatusBadge';
import UploadModal from '../components/polygons/UploadModal';
import PolygonMap from '../components/map/PolygonMap';
import BulkMetadataEditor from '../components/polygons/BulkMetadataEditor';
import PolygonDownloadMenu from '../components/polygons/PolygonDownloadMenu';
import { polygonsApi, getErrorMessage } from '../lib/api';

export default function PortfolioPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [filterQuery, setFilterQuery] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [polygons, setPolygons] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'table' | 'map' | 'edit'>('table');
    const [mapFeatures, setMapFeatures] = useState<any[]>([]);
    const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        fetchPolygons();
    }, []);

    const fetchPolygons = async () => {
        try {
            setLoading(true);
            setError(null);
            const listData = await polygonsApi.list({ page: 1, page_size: 100 });
            setPolygons(listData.items || []);
            // Fetch GeoJSON separately — don't block table on map data
            try {
                const geoData = await polygonsApi.getGeoJSON();
                setMapFeatures(geoData.features || []);
            } catch {
                // GeoJSON fetch failed, map will be empty but table still works
            }
        } catch (err: any) {
            setError(getErrorMessage(err) || 'Error loading polygons');
            toast.error('Error loading polygons');
        } finally {
            setLoading(false);
        }
    };

    const filteredPolygons = polygons.filter((p) => {
        if (!filterQuery) return true;
        const q = filterQuery.toLowerCase();
        return (
            p.name?.toLowerCase().includes(q) ||
            p.producer_name?.toLowerCase().includes(q) ||
            p.commodity?.toLowerCase().includes(q) ||
            p.country?.toLowerCase().includes(q)
        );
    });

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredPolygons.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredPolygons.map((p) => p.id)));
        }
    };

    const confirmBatchDelete = async () => {
        if (!selectedIds.size) return;
        const count = selectedIds.size;
        setShowDeleteConfirm(false);
        setDeleting(true);
        try {
            await polygonsApi.batchDelete({ polygon_ids: Array.from(selectedIds) });
            toast.success(`${count} polígono(s) eliminado(s)`);
            setSelectedIds(new Set());
            setSelectMode(false);
            fetchPolygons();
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al eliminar');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('polygons.title')}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400">
                        {t('polygons.portfolioCount', { count: polygons.length })}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* View mode toggle */}
                    <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                        <button
                            onClick={() => { setViewMode('table'); setSelectMode(false); setSelectedIds(new Set()); }}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'table'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Vista de tabla"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => { setViewMode('map'); setSelectMode(false); setSelectedIds(new Set()); }}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'map'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Vista de mapa"
                        >
                            <Map className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => { setViewMode('edit'); setSelectMode(false); setSelectedIds(new Set()); }}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'edit'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Edición masiva"
                        >
                            <Table2 className="w-4 h-4" />
                        </button>
                    </div>

                    <button
                        onClick={fetchPolygons}
                        disabled={loading}
                        className="btn-secondary flex items-center gap-2 text-sm"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    {viewMode === 'table' && !selectMode && filteredPolygons.length > 0 && (
                        <button
                            onClick={() => setSelectMode(true)}
                            className="btn-secondary flex items-center gap-2 text-sm"
                            title="Seleccionar polígonos"
                        >
                            <CheckSquare className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="btn-primary flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        {t('polygons.addPolygon')}
                    </button>
                </div>
            </div>

            {/* Filter */}
            <div className="relative">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    value={filterQuery}
                    onChange={(e) => { setFilterQuery(e.target.value); setSelectedIds(new Set()); setSelectMode(false); }}
                    placeholder="Filter by name, producer, commodity, country..."
                    className="input pl-12"
                />
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="card p-6 border-red-200">
                    <p className="text-red-600">{error}</p>
                    <button onClick={fetchPolygons} className="btn-secondary mt-3 text-sm">
                        Retry
                    </button>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && polygons.length === 0 && (
                <div className="card p-12 text-center">
                    <Map className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No polygons yet
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Upload your first file to get started
                    </p>
                    <button onClick={() => setShowUploadModal(true)} className="btn-primary">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload file
                    </button>
                </div>
            )}

            {/* MAP VIEW */}
            {!loading && !error && polygons.length > 0 && viewMode === 'map' && (
                <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-280px)]">
                    {/* Left panel: polygon list */}
                    <div className="w-full lg:w-80 flex-shrink-0 card overflow-hidden flex flex-col max-h-72 lg:max-h-none">
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700 font-semibold text-sm text-gray-700 dark:text-gray-300">
                            Polygons ({filteredPolygons.length})
                        </div>
                        <div className="overflow-y-auto flex-1">
                            {filteredPolygons.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedPolygonId(p.id)}
                                    className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                                        selectedPolygonId === p.id
                                            ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-l-primary-500'
                                            : ''
                                    }`}
                                >
                                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                        {p.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                        <span className="capitalize">{p.commodity}</span>
                                        {p.area_ha != null && <span>&middot; {p.area_ha.toFixed(1)} ha</span>}
                                    </div>
                                    {(p.region || p.country) && (
                                        <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                                            <MapPin className="w-3 h-3" />
                                            {[p.region, p.country].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Right panel: map */}
                    <div className="flex-1 card overflow-hidden h-[60vh] lg:h-full">
                        <PolygonMap
                            features={mapFeatures}
                            overlaps={[]}
                            showOverlaps={false}
                            selectedPolygonId={selectedPolygonId}
                            onPolygonClick={(id) => setSelectedPolygonId(id)}
                            height="100%"
                        />
                    </div>
                </div>
            )}

            {/* TABLE VIEW (read-only) */}
            {!loading && !error && filteredPolygons.length > 0 && viewMode === 'table' && (
                <>
                <div className="card overflow-hidden">
                    {/* Select mode toolbar */}
                    {selectMode && (
                        <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <input
                                type="checkbox"
                                checked={selectedIds.size === filteredPolygons.length && filteredPolygons.length > 0}
                                ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filteredPolygons.length; }}
                                onChange={toggleSelectAll}
                                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {selectedIds.size > 0
                                    ? `${selectedIds.size} de ${filteredPolygons.length} seleccionado(s)`
                                    : 'Selecciona polígonos para eliminar'}
                            </span>
                            {selectedIds.size > 0 && (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={deleting}
                                    className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                                >
                                    {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    Eliminar ({selectedIds.size})
                                </button>
                            )}
                            <button
                                onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
                                className="ml-auto p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                                title="Salir de selección"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                    {selectMode && <th className="py-3 pl-4 pr-2 w-10" />}
                                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Commodity
                                    </th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Location
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Area (ha)
                                    </th>
                                    <th className="hidden lg:table-cell text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Format
                                    </th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        EUDR
                                    </th>
                                    <th className="hidden lg:table-cell text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Updated
                                    </th>
                                    <th className="py-3 px-4 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filteredPolygons.map((polygon) => (
                                    <tr
                                        key={polygon.id}
                                        onClick={selectMode ? () => toggleSelect(polygon.id) : undefined}
                                        className={`transition-colors ${
                                            selectMode && selectedIds.has(polygon.id)
                                                ? 'bg-primary-50 dark:bg-primary-900/20'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                        } ${selectMode ? 'cursor-pointer' : ''}`}
                                    >
                                        {selectMode && (
                                            <td className="py-3 pl-4 pr-2" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(polygon.id)}
                                                    onChange={() => toggleSelect(polygon.id)}
                                                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                                />
                                            </td>
                                        )}
                                        <td className="py-3 px-4">
                                            <div>
                                                <button
                                                    onClick={() => navigate(`/polygons/${polygon.id}`)}
                                                    className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 hover:underline text-sm text-left"
                                                >
                                                    {polygon.name}
                                                </button>
                                                {polygon.producer_name && (
                                                    <p className="text-xs text-gray-500">{polygon.producer_name}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                                {polygon.commodity}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                                {polygon.country || polygon.region ? (
                                                    <>
                                                        <MapPin className="w-3 h-3" />
                                                        {[polygon.region, polygon.country].filter(Boolean).join(', ')}
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {polygon.area_ha != null ? polygon.area_ha.toFixed(2) : '—'}
                                            </span>
                                        </td>
                                        <td className="hidden lg:table-cell py-3 px-4 text-center">
                                            {polygon.source_format ? (
                                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                                    {polygon.source_format}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <StatusBadge
                                                validationStatus={polygon.validation_status || 'none'}
                                                analysisStatus={polygon.analysis_status || 'none'}
                                                complianceStatus={polygon.compliance_status}
                                            />
                                        </td>
                                        {/* EUDR column — binary compliance per Regulation 2023/1115:
                                             compliant (< 0.1 ha deforested post-2020) = Compliant (green)
                                             non_compliant (>= 0.1 ha) = Non-Compliant (red)
                                             inconclusive (low confidence) = Inconclusive (yellow)
                                             no analysis = Pending (gray) */}
                                        <td className="py-3 px-4 text-center">
                                            {polygon.compliance_status ? (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    polygon.compliance_status === 'compliant'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : polygon.compliance_status === 'non_compliant'
                                                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                }`}>
                                                    {polygon.compliance_status === 'compliant'
                                                        ? <ShieldCheck className="w-3 h-3" />
                                                        : <ShieldAlert className="w-3 h-3" />
                                                    }
                                                    {polygon.compliance_status === 'compliant' ? 'Compliant'
                                                        : polygon.compliance_status === 'non_compliant' ? 'Non-Compliant'
                                                        : 'Inconclusive'}
                                                </span>
                                            ) : polygon.analysis_status === 'processing' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                    Analyzing
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                                    Pending
                                                </span>
                                            )}
                                            {polygon.deforestation_pct != null && (
                                                <div className="text-[11px] text-gray-400 mt-0.5">
                                                    {polygon.deforestation_pct.toFixed(2)}%
                                                    {polygon.area_deforested_ha != null
                                                        ? ` · ${polygon.area_deforested_ha.toFixed(2)} ha`
                                                        : ''}
                                                    {polygon.baseline_year && polygon.monitoring_year
                                                        ? ` · ${polygon.baseline_year}–${polygon.monitoring_year}`
                                                        : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td className="hidden lg:table-cell py-3 px-4 text-right">
                                            <span className="text-xs text-gray-500">
                                                {new Date(polygon.updated_at).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                            <PolygonDownloadMenu polygonId={polygon.id} polygonName={polygon.name} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>
            )}

            {/* EDIT VIEW (Bulk Editor) */}
            {!loading && !error && polygons.length > 0 && viewMode === 'edit' && (
                <BulkMetadataEditor
                    polygons={filteredPolygons}
                    onSaveComplete={fetchPolygons}
                    onNavigateToPolygon={(id) => navigate(`/polygons/${id}`)}
                />
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <UploadModal
                    onClose={() => setShowUploadModal(false)}
                    onSuccess={fetchPolygons}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50">
                    <div className="card p-6 w-full max-w-md animate-slide-up">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Eliminar polígonos
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    ¿Estás seguro de que deseas eliminar{' '}
                                    <span className="font-semibold text-gray-900 dark:text-white">
                                        {selectedIds.size} polígono{selectedIds.size > 1 ? 's' : ''}
                                    </span>
                                    ? Esta acción no se puede deshacer.
                                </p>
                                {selectedIds.size <= 5 && (
                                    <ul className="mt-3 space-y-1">
                                        {Array.from(selectedIds).map((id) => {
                                            const p = polygons.find((pg) => pg.id === id);
                                            return (
                                                <li key={id} className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                                    {p?.name || id}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="btn-secondary text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmBatchDelete}
                                disabled={deleting}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Eliminar {selectedIds.size > 1 ? `(${selectedIds.size})` : ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
